// src/components/auth/UserLogin.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../apiClient'; // User App's API client
import { useParticipantAuth } from '../../context/ParticipantAuthContext';
import { PeraWalletConnect } from '@perawallet/connect';

// Import icons (ensure paths are correct and files exist in User App's assets)
// These should be copied from your admin app's assets or use new ones.
import googleIcon from '../../assets/icons/branding_guideline_sample_nt_sq_sl.svg';
import peraIcon from '../../assets/icons/pera-logomark-black.svg';

// Initialize Pera Wallet connection (TestNet: 416002, MainNet: 416001)
const peraWallet = new PeraWalletConnect();

const UserLogin = ({ programId, programName, programShortName, onSwitchToRegister, onLoginSuccess }) => {
    const navigate = useNavigate();
    const { login, isAuthenticated } = useParticipantAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState(''); // 'success', 'error', or 'info'
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (isAuthenticated) {
            // If already authenticated (e.g. token persisted),
            // onLoginSuccess should handle modal closure and navigation.
            // This direct call might be redundant if onLoginSuccess does this.
            if (typeof onLoginSuccess === 'function') {
                onLoginSuccess();
            }
        }
         // Reconnect to Pera Wallet session if previously connected
        peraWallet.reconnectSession().then((accounts) => {
            peraWallet.connector?.on("disconnect", handlePeraDisconnect);
        }).catch(console.error);

        return () => {
            peraWallet.connector?.off("disconnect", handlePeraDisconnect);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated]); // Removed onLoginSuccess from deps to avoid loop if it changes identity often

     const handlePeraDisconnect = () => {
        // You might want to update UI or state if Pera Wallet disconnects
        console.log("Pera Wallet disconnected by user/extension.");
        // If a Pera login was in progress, you might want to clear related loading states.
    };

    const handleApiResponse = (response, successMsg, loginMethod) => {
        setMessage(successMsg || `Login successful with ${loginMethod}!`);
        setMessageType('success');
        login(response.data.access_token); // Update auth context
        setIsLoading(false);
        if (typeof onLoginSuccess === 'function') {
            onLoginSuccess(); // Notify parent to close modal and navigate
        }
    };

    const handleApiError = (error, loginMethod) => {
        const errorDetail = error.response?.data?.detail || error.message || `${loginMethod} login failed.`;
        setMessage(errorDetail);
        setMessageType('error');
        setIsLoading(false);
    };

    const handleEmailSubmit = async (e) => {
        e.preventDefault();
        if (!programId) {
            setMessage('Program information is missing.');
            setMessageType('error');
            return;
        }
        setIsLoading(true);
        setMessage('');
        setMessageType('');
        try {
            const response = await apiClient.post(`/public/programs/${programId}/login`, { email, password });
            handleApiResponse(response, `Login successful! Welcome back to ${programName}.`, 'Email');
        } catch (error) {
            handleApiError(error, 'Email');
        }
    };

    const handleGoogleLogin = () => {
        if (!programId) {
            setMessage('Program information is missing for Google Login.');
            setMessageType('error');
            return;
        }
        setIsLoading(true);
        setMessage('Redirecting to Google...');
        setMessageType('info');
        // Backend handles redirect to Google & callback to User App's /auth/callback/participant/google
        window.location.href = `${apiClient.defaults.baseURL}/public/programs/${programId}/auth/google/login`;
    };

    const handlePeraLogin = async () => {
        if (!programId) {
            setMessage('Program information is missing for Pera Wallet Login.');
            setMessageType('error');
            return;
        }
        setIsLoading(true);
        setMessage('Connecting to Pera Wallet...');
        setMessageType('info');
        try {
            const accounts = await peraWallet.connect();
            if (!accounts || accounts.length === 0) {
                throw new Error("No Pera Wallet account selected or connection failed by user.");
            }
            const address = accounts[0];
            setMessage('Pera Wallet connected. Logging in...');
            const response = await apiClient.post(`/public/programs/${programId}/auth/pera/login`, { address });
            handleApiResponse(response, `Pera Wallet login successful for ${programName}!`, 'Pera Wallet');
        } catch (error) {
            if (error.message?.includes("Popup closed") || error.message?.includes("connection failed by user")) {
                 setMessage("Pera Wallet connection cancelled or failed.");
                 setMessageType('info');
                 setIsLoading(false);
            } else {
                handleApiError(error, 'Pera Wallet');
            }
        }
    };

    return (
        <div>
            {/* Title is now part of the Modal component itself via props */}
            <form onSubmit={handleEmailSubmit}>
                <div>
                    <label htmlFor="login-email">Email:</label>
                    <input
                        type="email" id="login-email" value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required disabled={isLoading} autoComplete="email"
                    />
                </div>
                <div>
                    <label htmlFor="login-password">Password:</label>
                    <input
                        type="password" id="login-password" value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required disabled={isLoading} autoComplete="current-password"
                    />
                </div>
                <button type="submit" disabled={isLoading || !email || !password}>
                    {isLoading && message.includes('Email') ? 'Logging in...' : 'Login with Email'}
                </button>
            </form>

            <button 
                className="google-btn" // Style this in Modal.css or a shared auth.css
                onClick={handleGoogleLogin} 
                disabled={isLoading}
                style={{ width: '100%', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginTop: '10px', backgroundColor: '#F2F2F2', color: 'var(--color-primary-dark)', border: '1px solid var(--color-grey-border)', borderRadius: '6px', cursor: 'pointer' }}
            >
                <img src={googleIcon} alt="Google" style={{ height: '20px' }}/> 
                {isLoading && message.includes('Google') ? 'Processing...' : 'Login with Google'}
            </button>

            <button 
                className="pera-btn" // Style this in Modal.css or a shared auth.css
                onClick={handlePeraLogin} 
                disabled={isLoading}
                style={{ width: '100%', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginTop: '10px', backgroundColor: '#FFDD55', color: 'var(--color-primary-dark)', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
            >
                <img src={peraIcon} alt="Pera Wallet" style={{ height: '20px' }}/> 
                {isLoading && message.includes('Pera') ? 'Connecting...' : 'Connect Pera Wallet'}
            </button>

            {message && (
                <div className={`message-container ${messageType === 'success' ? 'success-message' : messageType === 'error' ? 'error-message' : 'info-message'}`} style={{marginTop: '15px', textAlign:'center'}}>
                    {message}
                </div>
            )}

            <p style={{textAlign: 'center', marginTop: '20px'}}>
                New to {programName}?{' '}
                <button type="button" onClick={onSwitchToRegister} disabled={isLoading}>
                    Register here
                </button>
            </p>
        </div>
    );
};

export default UserLogin;